import * as React from 'react';
import { z } from 'zod';
import { Button } from './Button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './Dialog';
import {
  Form,
  FormField,
  type FormFieldControl,
  type FormFieldState,
  type ZodForm,
} from './Form';
import { Grid } from './Layout';
import { Select, type SelectOption } from './Select';

export const newImpoundSchema = z.object({
  dispatchId: z.string().min(1, 'Select a call.'),
  locationId: z.string().min(1, 'Select a storage location.'),
  /** The chosen location's name, carried for the API. */
  storageLot: z.string(),
});

export type NewImpoundValues = z.infer<typeof newImpoundSchema>;
export type NewImpoundForm = ZodForm<typeof newImpoundSchema>;

export interface NewImpoundCallOption {
  id: string;
  /** "1041 - Ford F-150 (Jane Doe)" */
  label: React.ReactNode;
}

export interface NewImpoundDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From `useZodForm(newImpoundSchema, …)`. */
  form: NewImpoundForm;
  /** Calls that can still be impounded. */
  calls: NewImpoundCallOption[];
  loadingCalls?: boolean;
  /**
   * The storage location picker — the app's own control, with its
   * create-a-location form, so it is handed in rather than drawn here. It
   * receives the bound `locationId` field, and its message shows under it.
   */
  locationPicker: (
    field: FormFieldControl<typeof newImpoundSchema, 'locationId'>,
    state: FormFieldState
  ) => React.ReactNode;
  /** The form was submitted (Enter never submits; the button does). */
  onSubmit: (values: NewImpoundValues) => void;
  creating?: boolean;
  /** The location picker is mid-way through creating a new location. */
  creatingLocation?: boolean;
  canSubmit?: boolean;
}

const FORM_ID = 'new-impound-form';

/**
 * The create-new-impound dialog on the impounds list: pick a call, pick (or
 * create) a storage location, Create Impound.
 *
 *   const form = useZodForm(newImpoundSchema, { defaultValues: EMPTY });
 *   <NewImpoundDialogView open={open} onOpenChange={setOpen} form={form}
 *     calls={calls}
 *     locationPicker={(field) => <LocationSelector value={field.value} onValueChange={field.onChange} … />}
 *     onSubmit={submit} creating={pending} />
 */
export function NewImpoundDialogView({
  open,
  onOpenChange,
  form,
  calls,
  loadingCalls = false,
  locationPicker,
  onSubmit,
  creating = false,
  creatingLocation = false,
  canSubmit = true,
}: NewImpoundDialogViewProps) {
  const callOptions: SelectOption[] = loadingCalls
    ? [{ value: 'loading', label: 'Loading calls...', disabled: true }]
    : calls.length
      ? calls.map((call) => ({ value: call.id, label: call.label }))
      : [{ value: 'no-calls', label: 'No calls available', disabled: true }];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Create New Impound</DialogTitle>
        </DialogHeader>
        <Form
          form={form}
          gap={6}
          id={FORM_ID}
          onSubmit={onSubmit}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter') e.preventDefault();
          }}
        >
          <Grid cols={{ base: 1, md: 2 }} gap={4}>
            <FormField form={form} name="dispatchId" label="Call/Dispatch">
              {(field) => (
                <Select
                  options={callOptions}
                  placeholder="Select a call"
                  value={field.value || undefined}
                  onValueChange={field.onChange}
                />
              )}
            </FormField>
          </Grid>

          {/* The picker draws its own label, so it is handed the marker and
              the invalid tone; this adds only the message. */}
          <FormField form={form} name="locationId">
            {(field, state) => locationPicker(field, state)}
          </FormField>
        </Form>

        {/* Outside the form so DialogContent can hoist it clear of the
            scrolling body; the submit button reaches the form by id. */}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            form={FORM_ID}
            disabled={creating || creatingLocation || !canSubmit}
            type="submit"
          >
            {creating
              ? 'Creating...'
              : creatingLocation
                ? 'Creating Location...'
                : 'Create Impound'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
